import React, { useState, useEffect, useCallback } from "react";
import "react-toastify/dist/ReactToastify.css"; //https://fkhadra.github.io/react-toastify/introduction/
import DeleteComponent from "../DeleteComponent";
import { DialogComponent } from "@syncfusion/ej2-react-popups";
import {
  createDocFromCollection,
  addOrUpdateDocFromPath,
  docRef,
  colRef,
} from "../../globals/services";
import ConfirmComponent from "../ConfirmComponent";
import { AddCircleOutline } from "@mui/icons-material";
import { Button, CircularProgress } from "@mui/material";
import { Box } from "@mui/system";
import SpDialog from "../../globals/SpDialog";
import { generateUniqueId } from "../../globals/helpers";

/**
 *
 * @param {object} props coming from the branding.js file
 * @returns
 */
const CertifiedPrograms = (props) => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [state, setState] = useState(false);
  const [website, setWebsite] = useState("");
  const [programName, setProgramName] = useState("");
  const [file, setFile] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [buttonText, setButtonText] = useState("Update Certified Programs");
  const [pitchDecks, setPitchDecks] = useState([]);

  const vendorData = props.vendorData;
  const vendorId = props.vendorData?.id;
  let storage = props.firebase.getStorage();

  /**
   * Arrays of file and upload each of them to the firebase storage and in firestore
   * @param {Array} files array of files
   * @returns a list of object containing fileName, fileRef, uploadedDate
   */

  /**
   * when the input button is clicked it will open the file upload dialog so users can select multiple files
   *
   * @param {*} e event of the input event
   */

  /**
   * takes 3 strings and find the data to be deleted then remove the data from the firebase
   * @param {String} fileName the name of the file to be deleted
   * @param {String} i index of the file to be deleted
   * @param {String} docId id of the file to be deleted in firebase
   */

  const deleteDoc = async (id) => {
    const delDoc = await docRef(`vendors/${vendorId}/certifiedPrograms/${id}`);
    delDoc.delete();
  };

  /**
   * click the input button when the custom button is clicked
   */
  const handleImageChange = (e) => {
    e.preventDefault();
    setFile(e.target.files[0]);
  };

  const checkProgramName = () => {
    return programs.filter((p) => p.programName === programName);
  };

  const validate = () => {
    if (file === "") {
      alert("Please select a file");
      return false;
    };
    if (programName === "") {
      alert("Please enter a company name");
      return false;
    };
    if (website === "") {
      alert("Please enter a website");
      return false;
    };
    return true;
  };

  const onSubmit = async (e, id) => {
    if (!validate()) return;
    setState(false);
    e.preventDefault();

    setUploading(true);

    try {
      // upload file to vendor storage path
      const uploadRef = storage.ref(`vendors/${vendorId}/certifiedPrograms/${file.name}`);
      const snapshot = await uploadRef.put(file);
      const fileUrl = await snapshot.ref.getDownloadURL();

      // generate uniqueId
      const uniqueId = generateUniqueId(programName, website);

      // certified program data
      const data = {
        website: website,
        programName: programName,
        fileRef: fileUrl,
        fileName: file.name,
        uniqueId,
        // uploadedDate: new Date(),
      };

      // check for existing program by uniqueId in vendor's certifiedPrograms subcol
      const vendorProgramsCol = colRef(`vendors/${vendorId}/certifiedPrograms`);
      const existingProgramByUniqueId = await vendorProgramsCol
        .where("uniqueId", "==", uniqueId)
        .limit(1)
        .get();

      let programDocRef;

      if (!existingProgramByUniqueId.empty) {
        // found by uniqueId -> update
        programDocRef = existingProgramByUniqueId.docs[0].ref;
        await programDocRef.update({
          ...data,
          updatedDate: props.firebase.fire.FieldValue.serverTimestamp(),
        });
      } else {
        // fallback: check by programName
        let existingProgram = checkProgramName();
        if (existingProgram.length > 0) {
          // update existing found by programName
          const path = `vendors/${vendorId}/certifiedPrograms/${existingProgram[0].id}`;
          await addOrUpdateDocFromPath(path, {
            ...data,
            updatedDate: props.firebase.fire.FieldValue.serverTimestamp(),
          });
          programDocRef = docRef(path);
        } else {
          // create new program doc in vendor's certifiedPrograms subcol
          const newDocRef = vendorProgramsCol.doc();
          await newDocRef.set({
            ...data,
            uploadedDate: props.firebase.fire.FieldValue.serverTimestamp(),
          });
          programDocRef = newDocRef;
        };
      };

      // call the HubSpot check/create company API
      const hsResponse = await fetch(
        `${props.firebase.spryteCloudUrl("checkAndCreateCompanyOnHubSpotAPI")}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            domain: website || "",
            companyName: programName || "",
            refPath: vendorData.vendorRef.path,
          }),
        }
      );
      const hsResult = await hsResponse.json();
      if (hsResult.error) throw new Error(hsResult.error);

      // if HubSpot returned an hsID, find the company in Firestore and link
      const hsID = Number(hsResult.hsID);
      if (hsID) {
        // check if the company already exists
        const companiesCol = colRef("companies");
        const existingCompanies = await companiesCol
          .where("hsID", "==", hsID)
          .limit(1)
          .get();

        if (!existingCompanies.empty) {
          const companyDoc = existingCompanies.docs[0];
          const companyRef = companyDoc.ref;

          // link program -> company
          await programDocRef.update({
            companyRef,
          });

          // link company -> vendor
          await companyRef.update({
            vendors: props.firebase.fire.FieldValue.arrayUnion(docRef(`vendors/${vendorId}`)),
          });
        };
      };

      setUploading(false);
      return {
        ...data,
        hsCompany: hsResult,
        programId: programDocRef.id
      };
    } catch (err) {
      console.error("onSubmit error:", err);
      setUploading(false);
    };
  };

  // stream certified programs
  useEffect(() => {
    const collectionRef = colRef(`vendors/${vendorId}/certifiedPrograms`);
    let subscribe = collectionRef.onSnapshot((snapshot) => {
      let data = snapshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
      setPrograms(data);
      setLoading(false);
    });
    return subscribe;
  }, [vendorId]);

  const onClose = useCallback(() => {
    setState(false);
  }, []);

  // get vendor documents
  useEffect(() => {
    const getVendorDocs = async () => {
      if (vendorData?.id) {
        try {
          const pitchDecks = await props.firebase.getDocuments(vendorId);

          setPitchDecks(pitchDecks);
        } catch (err) {
          console.error(err);
        };
      };
    };

    getVendorDocs();
  }, [isLoading, props.firebase, vendorData?.id]);

  // update vendor certified programs in Firestore
  // invokes the updateVendorCertifiedPrograms cloud function when the Update Certified Programs button is clicked
  const updateVendorCertifiedPrograms = async () => {
    setIsLoading(true);
    setButtonText('Updating Certified Programs...');

    try {
      const response = await fetch(`${props.firebase.spryteCloudUrl("updateVendorCertifiedProgramsAPI")}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vendorBio: vendorData.profile.bio || "",
          companyName: vendorData.companyName || "",
          techFocus: vendorData.techFocus || "",
          website: vendorData.website || "",
          companyURL: vendorData.website || "",
          vendorRefPath: vendorData.vendorRef.path,
          pitchDeck: pitchDecks || [],
        }),
      });
      const result = await response.json();
      if (result.error) throw new Error(result.error);

      console.log("Certified programs updated:", result.message);
    } catch (error) {
      console.error("Error updating certified programs:", error);
    } finally {
      setIsLoading(false);
      setButtonText("Update Certified Programs");
    };
  };

  return (
    <>
      <div className="contract_cont">
        <div className="ctr security">
          <div className="ctr_el_header resource-header">
            <div className="header-intro">
              <h3>Certified Partner Programs</h3>
              <AddCircleOutline
                className="icon"
                onClick={() => setState(true)}
              />

              {uploading ? (
                <CircularProgress color="primary" size="small" />
              ) : (
                <></>
              )}
            </div>

            <div className="content">
              <p className="lighter" style={{ marginTop: "-8.5px" }}>
                Update certified partner programs you belong to.
                You can also do that automatically by clicking the Update Certified Programs button.
              </p>
              <button
                onClick={updateVendorCertifiedPrograms}
                disabled={isLoading}
                className="content__sp_update-clients-btn"
              >
                <img
                  src={"/Spryte-AI-1.png"}
                  alt="Spryte AI logo"
                  width={25}
                  height={25}
                />
                <span>{buttonText}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="contract_bottom">
          {programs.length !== 0 ? (
            <table className="stable">
              <thead className="stable_header">
                <tr>
                  <th>File</th>
                  <th>Program Name</th>
                  <th>Program Domain</th>
                  <th>Uploaded Date</th>
                  <th>Download</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(programs).map((logo, id) => (
                  <tr key={`${programs[logo].fileName}${id}`}>
                    <td>{programs[logo].fileName}</td>
                    <td>{programs[logo].programName}</td>
                    <td>{programs[logo].website}</td>
                    <td>{programs[logo].uploadedDate?.toDate().toDateString() || "N/A"}</td>
                    <td>
                      <a
                        className="download"
                        href={programs.fileRef}
                        target="blank"
                      >
                        Download
                      </a>
                    </td>
                    <td>
                      <DeleteComponent
                        onClick={() => deleteDoc(programs[logo].id)}
                        id={programs[logo].id}
                        delete={deleteDoc}
                        fileName={programs[logo].fileName}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>You do not have any certified partner programs.</p>
          )}
        </div>
      </div>

      <SpDialog open={state} onClose={onClose}>
        <div style={{ display: "block", padding: 10 }} className="d">
          <form className="brand_dialog-box__inputs column">
            <section className="column section client_info">
              <div className="client_upload column info_input domain " mb={5}>
                <label>Program Domain</label>
                <input
                  type="text"
                  onChange={(e) => setWebsite(e.target.value)}
                  value={props.website}
                  placeholder="www.cloud.google.com"
                />
              </div>

              <div className="client_upload column  info_input name" mb={5}>
                <label>Program Name</label>
                <input
                  type="text"
                  onChange={(e) => setProgramName(e.target.value)}
                  value={props.programName}
                  placeholder="e.g. Google Cloud Partner"
                />
              </div>

              <div className="client_upload column info_input name">
                <label htmlFor="file">Add Logo:</label>
                <input
                  type="file"
                  id="file"
                  name="file"
                  accept=".png, .jpg, .jpeg"
                  onChange={(e) => handleImageChange(e)}
                />
              </div>
            </section>
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                console.log("clicked");
                onSubmit(e);
              }}
            >
              Submit
            </Button>
            {/* <ConfirmComponent
                onSubmit={onSubmit}
                programName={programName}
                setState={setState}
              /> */}
          </form>
        </div>
      </SpDialog>
    </>
  );
};

export default CertifiedPrograms;
